/**
 * Sales > Fair Share & Distribution — share of distribution (TDP) against share
 * of sales, per brand. Above the diagonal, a brand sells more than its shelf
 * presence suggests (a case for more distribution); below it, the reverse.
 */

import { useMemo } from 'react'
import type { EChartsOption } from 'echarts'

import Chart from '../../components/Chart'
import { DataTable } from '../../components/DataTable'
import type { CustomReportProps } from '../index'
import {
  BAD,
  GOOD,
  chart,
  colorsFor,
  money,
  num,
  pct,
  pctAxis,
  records,
} from './shared'
import { Empty, Panel, Tile } from './ui'

const LABELLED = 12

export function FairShare({ result, department }: CustomReportProps) {
  const { accent, second: neutral } = colorsFor(department)
  const rows = records(result)

  const summary = useMemo(() => {
    const scored = rows.filter((r) => num(r.fair_share_index) !== null && (num(r.dollar_share) ?? 0) >= 0.01)
    const under = scored.filter((r) => (num(r.fair_share_index) ?? 100) < 80)
    const over = scored.filter((r) => (num(r.fair_share_index) ?? 100) > 120)
    return { under, over }
  }, [rows])

  const bubbleOption = useMemo<EChartsOption>(() => {
    const maxSales = Math.max(1, ...rows.map((r) => num(r.dollar_sales) ?? 0))
    const maxShare = Math.max(
      0.05,
      ...rows.map((r) => Math.max(num(r.dollar_share) ?? 0, num(r.tdp_share) ?? 0)),
    )
    return {
      ...chart.baseOption(),
      grid: { left: 8, right: 24, top: 16, bottom: 36, containLabel: true },
      legend: { show: false },
      tooltip: {
        trigger: 'item',
        formatter: (p: unknown) => {
          const d = (p as { data: { row: Record<string, unknown> } }).data.row
          return [
            `<b>${String(d.brand)}</b>`,
            `Dollar share ${pct(d.dollar_share as number)}`,
            `Share of TDP ${pct(d.tdp_share as number)}`,
            `Index ${String(Math.round((d.fair_share_index as number | null) ?? 0))}`,
            `Sales ${money(d.dollar_sales as number)}`,
          ].join('<br>')
        },
      },
      xAxis: {
        type: 'value',
        name: 'Share of distribution (TDP)',
        nameLocation: 'middle',
        nameGap: 26,
        max: maxShare,
        axisLabel: { ...chart.axisLabel(), formatter: pctAxis },
        splitLine: chart.splitLine(),
      },
      yAxis: {
        type: 'value',
        name: 'Dollar share',
        max: maxShare,
        axisLabel: { ...chart.axisLabel(), formatter: pctAxis },
        splitLine: chart.splitLine(),
      },
      series: [
        {
          type: 'scatter',
          data: rows.map((r, i) => {
            const index = num(r.fair_share_index) ?? 100
            return {
              value: [num(r.tdp_share) ?? 0, num(r.dollar_share) ?? 0],
              row: r,
              symbolSize: 8 + 40 * Math.sqrt((num(r.dollar_sales) ?? 0) / maxSales),
              itemStyle: {
                color: index < 80 ? GOOD : index > 120 ? BAD : neutral,
                opacity: 0.75,
              },
              label: {
                show: i < LABELLED,
                formatter: String(r.brand),
                position: 'right',
                fontSize: 11,
              },
            }
          }),
          markLine: {
            silent: true,
            symbol: 'none',
            lineStyle: { type: 'dashed', color: accent },
            label: { formatter: 'Fair share', position: 'end' },
            data: [
              [
                { coord: [0, 0] },
                { coord: [maxShare, maxShare] },
              ],
            ],
          },
        },
      ],
    }
  }, [rows, accent, neutral])

  const indexOption = useMemo<EChartsOption>(() => {
    const top = rows.slice(0, 15).reverse()
    return {
      ...chart.baseOption(),
      grid: { left: 8, right: 24, top: 8, bottom: 8, containLabel: true },
      legend: { show: false },
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      xAxis: { type: 'value', axisLabel: chart.axisLabel(), splitLine: chart.splitLine() },
      yAxis: {
        type: 'category',
        data: top.map((r) => String(r.brand)),
        axisLabel: { ...chart.axisLabel(), fontFamily: 'IBM Plex Sans, sans-serif' },
        axisTick: { show: false },
        axisLine: chart.axisLine(),
      },
      series: [
        {
          name: 'Fair share index',
          type: 'bar',
          barMaxWidth: 14,
          data: top.map((r) => {
            const v = num(r.fair_share_index)
            return {
              value: v === null ? null : Math.round(v),
              itemStyle: { color: (v ?? 100) < 80 ? GOOD : (v ?? 100) > 120 ? BAD : neutral },
            }
          }),
          markLine: {
            silent: true,
            symbol: 'none',
            lineStyle: { type: 'dashed', color: accent },
            label: { formatter: '100' },
            data: [{ xAxis: 100 }],
          },
        },
      ],
    }
  }, [rows, accent, neutral])

  if (rows.length === 0) return <Empty>No sales for these filters.</Empty>

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Tile label="Brands" value={String(rows.length)} />
        <Tile
          label="Under-distributed (index < 80)"
          value={String(summary.under.length)}
          delta={summary.under.slice(0, 3).map((r) => String(r.brand)).join(', ') || undefined}
          deltaTone="up"
        />
        <Tile
          label="Over-distributed (index > 120)"
          value={String(summary.over.length)}
          delta={summary.over.slice(0, 3).map((r) => String(r.brand)).join(', ') || undefined}
          deltaTone="down"
        />
      </div>
      <div className="grid grid-cols-12 gap-4">
        <Panel
          title="Sales share vs distribution share"
          subtitle="Bubble size = dollar sales. Above the line: selling more than shelf presence suggests (green = index under 80)."
        >
          <Chart option={bubbleOption} height={380} ariaLabel="Dollar share versus distribution share" />
        </Panel>
        <Panel
          title="Fair share of distribution index — top 15 brands"
          subtitle="Share of TDP ÷ dollar share × 100. Under 100: under-distributed for its sales."
        >
          <Chart option={indexOption} height={380} ariaLabel="Fair share index by brand" />
        </Panel>
      </div>
      <DataTable columns={result.columns} rows={result.rows} caption="Fair share by brand" />
    </div>
  )
}
